package com.anjuros.website.redesign.sections.blogarticle

import androidx.compose.runtime.Composable
import androidx.compose.runtime.DisposableEffect
import androidx.compose.runtime.getValue
import androidx.compose.runtime.mutableStateOf
import androidx.compose.runtime.remember
import androidx.compose.runtime.setValue
import com.anjuros.website.components.language.LocalText
import com.anjuros.website.redesign.components.RdTag
import com.anjuros.website.redesign.sections.blog.RdBlogGuide
import com.anjuros.website.redesign.styles.ArticleLayoutStyle
import com.anjuros.website.redesign.styles.ArticleProgressStyle
import com.anjuros.website.redesign.styles.ArticleTocSignStyle
import com.anjuros.website.redesign.styles.ArticleTocStyle
import com.anjuros.website.redesign.styles.ArticleTocSummaryStyle
import com.anjuros.website.redesign.styles.RdWrapStyle
import com.anjuros.website.redesign.util.RdText
import com.varabyte.kobweb.compose.ui.Modifier
import com.varabyte.kobweb.compose.ui.attrsModifier
import com.varabyte.kobweb.compose.ui.modifiers.id
import com.varabyte.kobweb.compose.ui.modifiers.onClick
import com.varabyte.kobweb.compose.ui.toAttrs
import com.varabyte.kobweb.silk.style.toModifier
import kotlinx.browser.document
import kotlinx.browser.window
import org.jetbrains.compose.web.dom.A
import org.jetbrains.compose.web.dom.Div
import org.jetbrains.compose.web.dom.Li
import org.jetbrains.compose.web.dom.Ol
import org.jetbrains.compose.web.dom.Span
import org.jetbrains.compose.web.dom.Text
import org.w3c.dom.HTMLElement
import org.w3c.dom.events.Event

private const val PROGRESS_ID = "article-progress"

/** Same width as Breakpoint.LG, where "On this page" moves beside the text and stays open. */
private const val WIDE_QUERY = "(min-width: 62em)"

/** The article text with "On this page" beside it (or above it on small screens) and a reading progress bar. */
@Composable
fun ArticleBodySection(guide: RdBlogGuide) {
    val titles = guide.card.blogSectionList.map { LocalText(it.sectionTitle) }
    val ids = titles.map(::articleSectionId)
    var activeId by remember { mutableStateOf(ids.firstOrNull()) }
    var isWide by remember { mutableStateOf(window.matchMedia(WIDE_QUERY).matches) }
    var tocOpen by remember { mutableStateOf(false) }

    DisposableEffect(ids) {
        val onScroll: (Event) -> Unit = {
            // The section being read is the last one whose top has passed 30% of the screen height.
            val line = window.innerHeight * 0.3
            activeId = ids.lastOrNull { id -> (document.getElementById(id)?.getBoundingClientRect()?.top ?: Double.MAX_VALUE) <= line }
                ?: ids.firstOrNull()
            val article = document.querySelector("main article")
            val bar = document.getElementById(PROGRESS_ID) as? HTMLElement
            if (article != null && bar != null) {
                val box = article.getBoundingClientRect()
                val scrollable = (box.height - window.innerHeight).coerceAtLeast(1.0)
                bar.style.width = "${(-box.top / scrollable).coerceIn(0.0, 1.0) * 100}%"
            }
        }
        val onResize: (Event) -> Unit = { isWide = window.matchMedia(WIDE_QUERY).matches }
        window.addEventListener("scroll", onScroll)
        window.addEventListener("resize", onResize)
        onScroll(Event("scroll"))
        onDispose {
            window.removeEventListener("scroll", onScroll)
            window.removeEventListener("resize", onResize)
        }
    }

    Div(ArticleProgressStyle.toModifier().id(PROGRESS_ID).attrsModifier { attr("aria-hidden", "true") }.toAttrs())
    Div(RdWrapStyle.toModifier().then(ArticleLayoutStyle.toModifier()).toAttrs()) {
        val open = isWide || tocOpen
        RdTag("details", ArticleTocStyle.toModifier().attrsModifier { if (open) attr("open", "") }) {
            RdTag("summary", ArticleTocSummaryStyle.toModifier().onClick { event ->
                event.preventDefault()
                if (!isWide) tocOpen = !tocOpen
            }) {
                Span { Text(LocalText(RdText.BLOG_TOC_TITLE)) }
                Span(ArticleTocSignStyle.toModifier().attrsModifier { attr("aria-hidden", "true") }.toAttrs()) {
                    Text(if (open) "−" else "+")
                }
            }
            Ol {
                titles.zip(ids).forEach { (title, id) ->
                    Li {
                        A("#$id", {
                            if (id == activeId) attr("aria-current", "true")
                            onClick { tocOpen = false }
                        }) { Text(title) }
                    }
                }
            }
        }
        ArticleContent(guide)
    }
}
